import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import leaveService from '@/services/leaveService'

const initialState = {
  leaveTypes: [],
  quotas: [],
  requests: [],
  currentRequest: null,
  statistics: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    perPage: 20,
    totalPages: 0,
  },
}

// Async thunks
export const fetchLeaveTypes = createAsyncThunk(
  'leave/fetchLeaveTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaveService.getLeaveTypes()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave types')
    }
  }
)

export const fetchQuotas = createAsyncThunk(
  'leave/fetchQuotas',
  async (params, { rejectWithValue }) => {
    try {
      const response = await leaveService.getQuotas(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch quotas')
    }
  }
)

export const createLeaveRequest = createAsyncThunk(
  'leave/createRequest',
  async (data, { rejectWithValue }) => {
    try {
      const response = await leaveService.createRequest(data)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create leave request')
    }
  }
)

export const fetchLeaveRequests = createAsyncThunk(
  'leave/fetchRequests',
  async (params, { rejectWithValue }) => {
    try {
      const response = await leaveService.getRequests(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leave requests')
    }
  }
)

export const fetchStatistics = createAsyncThunk(
  'leave/fetchStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await leaveService.getStatistics(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch statistics')
    }
  }
)

const leaveSlice = createSlice({
  name: 'leave',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leave Types
      .addCase(fetchLeaveTypes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLeaveTypes.fulfilled, (state, action) => {
        state.loading = false
        state.leaveTypes = action.payload.data
      })
      .addCase(fetchLeaveTypes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Quotas
      .addCase(fetchQuotas.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchQuotas.fulfilled, (state, action) => {
        state.loading = false
        state.quotas = action.payload.data.quotas
      })
      .addCase(fetchQuotas.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Create Request
      .addCase(createLeaveRequest.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createLeaveRequest.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(createLeaveRequest.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Requests
      .addCase(fetchLeaveRequests.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchLeaveRequests.fulfilled, (state, action) => {
        state.loading = false
        state.requests = action.payload.data.items
        state.pagination = action.payload.data.pagination
      })
      .addCase(fetchLeaveRequests.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Statistics
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload.data
      })
  },
})

export const { clearError } = leaveSlice.actions
export default leaveSlice.reducer
